import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, throwError } from 'rxjs';
import { AcademyAdministratorOperation } from '../../../../../model/administrator/AcademyAdministratorOperation';
import { AcademyAdministratorType } from '../../../../../model/administrator/AcademyAdministratorType';
import { AdministratorEntity } from '../../../../../model/administrator/AdministratorEntity';
import { Club } from '../../../../../model/Club';
import { Competition } from '../../../../../model/competitions/Competition';
import { HockeyCategory } from '../../../../../model/competitions/HockeyCategory';
import { CategoryLevel } from '../../../../../model/competitions/Level';
import { TeamRegistration } from '../../../../../model/competitions/TeamRegistration';
import { RegistrationPaymentStatus } from '../../../../../model/enum/RegistrationPaymentStatus';
import { RegistrationStatus } from '../../../../../model/enum/RegistrationStatus';
import { HttpResponse } from '../../../../../model/HttpResponse';
import { Member } from '../../../../../model/member';
import { QueryOptionDto } from '../../../../../model/QueryOptionDto';
import { ClubService } from '../../../../../services/club.service';
import { CompetitionService } from '../../../../../services/competition.service';
import { GestionService } from '../../../../../services/gestion.service';
import { LoadingService } from '../../../../../services/loading.service';
import { ExcelReport, ReportService } from '../../../../../services/report.service';
import { UtilsService } from '../../../../../services/utils.service';
import { HockeyInscriptionFeeModalComponent } from '../hockey-inscription-fee-modal/hockey-inscription-fee-modal.component';
import { HockeyTeamMembersModalComponent } from '../hockey-team-members-modal/hockey-team-members-modal.component';

@Component({
  selector: 'app-hockey-competition-detail',
  standalone: true,
  imports: [CommonModule, ToastModule, TabViewModule, TableModule, TagModule, ButtonModule, TooltipModule, HockeyTeamMembersModalComponent, HockeyInscriptionFeeModalComponent],
  providers: [MessageService],
  templateUrl: './hockey-competition-detail.component.html',
  styleUrl: './hockey-competition-detail.component.scss',
})
export class HockeyCompetitionDetailComponent implements OnInit {
  @Input()
  competition!: Competition;

  @Input()
  categories: HockeyCategory[] = [];

  @Input()
  Levels: CategoryLevel[] = [];

  @Output()
  onFormClosed: EventEmitter<boolean> = new EventEmitter<boolean>();

  teamRegistrations: TeamRegistration[] = [];
  sortedTeamRegistrations: TeamRegistration[] = [];
  selectedCategoryId: string = '';
  activeTabIndex: number = 0;
  clubsMap: { [clubId: string]: Club } = {};
  membersMap: { [memberId: string]: Member } = {}; // Mapa de miembros cargados
  showMembersModal: boolean = false;
  selectedTeam: TeamRegistration | null = null;
  selectedCategory: HockeyCategory | null = null;
  showInscriptionFeeModal: boolean = false;
  selectedFeeCategory: HockeyCategory | null = null;
  
  get competitionCategories(): HockeyCategory[] {
    return this.categories.filter(category => 
      this.competition && this.competition.categories?.some(compCategory => compCategory.id === category.id)
    ) || this.categories;
  }

  constructor(
    private competitionService: CompetitionService,
    private gestionService: GestionService,
    private loadingService: LoadingService,
    private messageService: MessageService,
    private clubService: ClubService,
    private reportService: ReportService
  ) {}

  ngOnInit(): void {
    this.loadClubs();
    if (this.competitionCategories.length > 0) {
      this.selectedCategoryId = this.competitionCategories[0].id;
      this.getTeamRegistrationsByCategory(this.selectedCategoryId);
    }
  }

  onTabChange(event: any): void {
    this.activeTabIndex = event.index;
    if (this.competitionCategories[this.activeTabIndex]) {
      this.selectedCategoryId = this.competitionCategories[this.activeTabIndex].id;
      this.getTeamRegistrationsByCategory(this.selectedCategoryId);
    }
  }

  getTeamRegistrationsByCategory(categoryId: string): void {
    this.loadingService.showLoading();
    const options: QueryOptionDto = {
      options: {
        conditionals: [
          {
            conditionalConnector: 'AND',
            logicOperator: '=',
            evaluatedProperty: 'competitionId',
            parameterName: '@CompetitionId',
            parameterValue: this.competition.id,
          },
          {
            conditionalConnector: 'AND',
            logicOperator: '=',
            evaluatedProperty: 'categoryId',
            parameterName: '@CategoryId',
            parameterValue: categoryId,
          }
        ],
      },
    };

    const entity: AdministratorEntity = {
      type: AcademyAdministratorType.REGISTRATIONS,
      operation: AcademyAdministratorOperation.SELECT,
      payload: options,
    };

    this.competitionService
      .sendRequest(entity)
      .pipe(catchError(this.handleError.bind(this)))
      .subscribe((response: HttpResponse) => {
        if (response.code === 200 && response.response) {
          this.teamRegistrations = response.response as TeamRegistration[];
          this.sortTeamsByClub();
          // Cargar datos completos de todos los miembros para verificación de edades
          this.loadAllMembersForCategory();
        } else {
          this.messageService.add({
            severity: 'info',
            summary: 'Sin equipos',
            detail: 'No se encontraron equipos para la categoría seleccionada',
            life: 5000,
          });
          this.teamRegistrations = [];
          this.sortedTeamRegistrations = [];
          this.loadingService.hideLoading();
        }
      });
  }

  private sortTeamsByClub(): void {
    this.sortedTeamRegistrations = [...this.teamRegistrations].sort((a, b) => {
      const clubNameA = this.getClubName(a.clubId);
      const clubNameB = this.getClubName(b.clubId);
      return clubNameA.localeCompare(clubNameB);
    });
  }

  private loadClubs(): void {
    this.clubService
      .getAllClubs()
      .pipe(catchError(this.handleClubError.bind(this)))
      .subscribe((response: HttpResponse) => {
        if (response.code === 200 && response.response) {
          const clubs = response.response as Club[];
          this.clubsMap = {};
          clubs.forEach((club: Club) => {
            if (club.id) {
              this.clubsMap[club.id] = club;
            }
          });
        }
      });
  }

  private handleClubError(error: any) {
    console.warn('Error loading clubs:', error);
    this.clubsMap = {};
    return throwError(() => error);
  }

  getClubName(clubId: string): string {
    return this.clubsMap[clubId]?.name || `Club ID: ${clubId}`;
  }

  getStatusLabel(status: RegistrationStatus): string {
    switch (status) {
      case RegistrationStatus.PRE_REGISTERED:
        return 'Preinscrito';
      case RegistrationStatus.REGISTERED:
        return 'Inscrito';
      default:
        return status;
    }
  }

  getStatusSeverity(status: RegistrationStatus): string {
    switch (status) {
      case RegistrationStatus.REGISTERED:
        return 'success';
      case RegistrationStatus.PRE_REGISTERED:
        return 'warning';
      default:
        return 'danger';
    }
  }

  getPaymentSeverity(paymentStatus: RegistrationPaymentStatus): string {
    switch (paymentStatus) {
      case RegistrationPaymentStatus.PAYMENT_CONFIRMED:
        return 'success';
      case RegistrationPaymentStatus.PAYMENT_PENDING:
        return 'warning';
      case RegistrationPaymentStatus.PAYMENT_FAILED:
        return 'danger';
      default:
        return 'info';
    }
  }

  getTeamCountForClub(clubId: string): number {
    return this.sortedTeamRegistrations.filter(team => team.clubId === clubId).length;
  }

  /**
   * Carga los datos completos de todos los miembros de la categoría actual
   * para poder verificar edades
   */
  private loadAllMembersForCategory(): void {
    // Recopilar todos los IDs de miembros únicos de todos los equipos
    const allMemberIds = new Set<string>();
    this.teamRegistrations.forEach(team => {
      if (team.members) {
        team.members.forEach(member => allMemberIds.add(member.memberId));
      }
    });

    if (allMemberIds.size === 0) {
      this.loadingService.hideLoading();
      return;
    }

    // Crear conditionals para obtener todos los miembros
    const memberConditionals = Array.from(allMemberIds).map((memberId, index) => ({
      conditionalConnector: 'OR',
      logicOperator: '=',
      evaluatedProperty: 'id',
      parameterName: `@MemberId${index}`,
      parameterValue: memberId,
    }));

    const options: QueryOptionDto = {
      options: {
        conditionals: memberConditionals,
      },
    };

    const entity: AdministratorEntity = {
      type: AcademyAdministratorType.MEMBERS,
      operation: AcademyAdministratorOperation.SELECT,
      payload: options,
    };

    this.gestionService
      .sendRequest(entity)
      .pipe(catchError((error) => {
        this.loadingService.hideLoading();
        return throwError(() => error);
      }))
      .subscribe((response: HttpResponse) => {
        if (response.code === 200 && response.response) {
          const members = response.response as Member[];
          // Crear mapa de miembros por ID para acceso rápido
          this.membersMap = {};
          members.forEach(member => {
            if (member.id) {
              this.membersMap[member.id] = member;
            }
          });
        }
        this.loadingService.hideLoading();
      });
  }

  /**
   * Cuenta el número de miembros con error de categoría en un equipo
   */
  countMembersWithAgeError(team: TeamRegistration): number {
    if (!team.members || team.members.length === 0) {
      return 0;
    }

    const category = this.competitionCategories.find(cat => cat.id === team.categoryId);
    if (!category) {
      return 0;
    }

    return team.members.filter(teamMember => {
      // Buscar el miembro completo en el mapa
      const member = this.membersMap[teamMember.memberId];
      if (!member || !member.birthDay) return false;

      // Solo verificar deportistas
      if (member.rol !== 'DEPORTISTA') return false;

      return UtilsService.hasCategoryAgeError(member.birthDay, category, this.competition);
    }).length;
  }

  /**
   * Verifica si un equipo tiene al menos un miembro con error de categoría
   */
  hasTeamAgeErrors(team: TeamRegistration): boolean {
    return this.countMembersWithAgeError(team) > 0;
  }

  showTeamMembers(team: TeamRegistration): void {
    this.selectedTeam = team;
    this.selectedCategory = this.competitionCategories.find(cat => cat.id === this.selectedCategoryId) || null;
    this.showMembersModal = true;
  }

  onMembersModalClose(): void {
    this.showMembersModal = false;
    this.selectedTeam = null;
    this.selectedCategory = null;
  }

  showInscriptionFeeConfiguration(category: HockeyCategory): void {
    this.selectedFeeCategory = category;
    this.showInscriptionFeeModal = true;
  }

  onInscriptionFeeModalClose(saved: boolean = false): void {
    this.showInscriptionFeeModal = false;
    this.selectedFeeCategory = null;
    
    if (saved) {
      // Refresh competition data if needed
      this.messageService.add({
        severity: 'success',
        summary: 'Actualizado',
        detail: 'El valor de inscripción ha sido actualizado correctamente',
        key: 'inscriptionFeeUpdate',
        life: 3000,
      });
    }
  }

  getInscriptionFee(categoryId: string): number {
    if (!this.competition.customData) return 0;
    const customData = this.competition.customData as any;
    const inscriptionFees = customData.inscriptionFees || {};
    return inscriptionFees[categoryId] || 0;
  }

  formatCurrency(value: number): string {
    if (value <= 0) return 'No definido';
    return new Intl.NumberFormat('es-CO', {

      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value);
  }

  goBack(): void {
    this.onFormClosed.emit(false);
  }

  exportCategoryPlayerTemplate(): void {
    if (!this.selectedCategoryId || this.sortedTeamRegistrations.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Sin datos',
        detail: 'No hay equipos registrados para exportar en esta categoría',
        life: 3000,
      });
      return;
    }

    this.loadingService.showLoading();
    this.getTeamMembersForCategory(this.selectedCategoryId).then((allMembersData) => {
      if (allMembersData.length === 0) {
        this.messageService.add({
          severity: 'info',
          summary: 'Sin jugadores',
          detail: 'No hay jugadores registrados en esta categoría',
          life: 3000,
        });
        this.loadingService.hideLoading();
        return;
      }

      const categoryName = this.competitionCategories.find(cat => cat.id === this.selectedCategoryId)?.name || 'Categoría';
      
      // Crear encabezado informativo sin títulos de columnas
      const infoData = [
        ['COMPETENCIA:', this.competition.name],
        ['CATEGORÍA:', categoryName],
        ['FECHA GENERACIÓN:', new Date().toLocaleDateString('es-CO')],
        [''],
        [''],
        [''],
        [''],
        [''], // Fila 8 vacía
        ['Nombre', 'Apellido', 'Tipo de Documento', 'Número de Documento', 'Género', 'Fecha de Nacimiento', 'Club', 'Equipo', 'Número de Juego', 'Rol', 'Nivel'] // Fila 9 con encabezados
      ];

      // Datos de los jugadores como arrays
      const playersData = allMembersData.map(memberData => [
        memberData.member.name,
        memberData.member.lastName,
        memberData.member.idType || '',
        memberData.member.idNumber,
        memberData.member.gender,
        memberData.member.birthDay,
        memberData.member.club?.name || '',
        memberData.team.name,
        memberData.jerseyNumber || '',
        memberData.member.rol || '',
        memberData.member.nivel || ''
      ]);

      // Combinar información, encabezados y datos de jugadores
      const exportData = [...infoData, ...playersData];

      const reportData = [{
        data: exportData,
        sheetName: categoryName,
        skipHeader: true,
        formatTables: true
      }];

      const fileName = `Plantilla_Jugadores_${this.competition.name}_${categoryName}_${new Date().toISOString().split('T')[0]}`;
      this.reportService.exportExcel(reportData, fileName);
      
      this.messageService.add({
        severity: 'success',
        summary: 'Exportación exitosa',
        detail: `Se exportaron ${allMembersData.length} jugadores de la categoría ${categoryName}`,
        life: 3000,
      });
      
      this.loadingService.hideLoading();
    }).catch((error) => {
      this.loadingService.hideLoading();
      this.messageService.add({
        severity: 'error',
        summary: 'Error en exportación',
        detail: 'No se pudo exportar la plantilla de jugadores',
        life: 5000,
      });
    });
  }

  exportTeamPlayerTemplate(team: TeamRegistration): void {
    if (!team.members || team.members.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Sin miembros',
        detail: 'Este equipo no tiene miembros registrados',
        life: 3000,
      });
      return;
    }

    this.loadingService.showLoading();
    this.getTeamMembers(team.members.map(m => m.memberId)).then((members) => {
      if (members.length === 0) {
        this.messageService.add({
          severity: 'info',
          summary: 'Sin datos',
          detail: 'No se pudieron obtener los datos de los jugadores',
          life: 3000,
        });
        this.loadingService.hideLoading();
        return;
      }

      const clubName = this.getClubName(team.clubId);
      const categoryName = this.competitionCategories.find(cat => cat.id === this.selectedCategoryId)?.name || 'Categoría';

      // Crear encabezado informativo sin títulos de columnas
      const infoData = [
        ['EQUIPO:', team.name],
        ['CLUB:', clubName],
        ['CATEGORÍA:', categoryName],
        ['FECHA GENERACIÓN:', new Date().toLocaleDateString('es-CO')],
        [''],
        [''],
        [''],
        [''], // Fila 8 vacía
        [ 'Rol','Número de Juego','Nombre', 'Apellido', 'Tipo de Documento', 'Número de Documento', 'Género', 'Fecha de Nacimiento' ] // Fila 9 con encabezados
      ];

      // Datos de los jugadores como arrays
      const playersData = members.map(member => {
        const teamMemberData = team.members?.find(m => m.memberId === member.id);
        const jerseyNumber = teamMemberData?.customData?.playerNumber;
        
        return [
          member.rol || '',
          jerseyNumber || '',
          member.name,
          member.lastName,
          member.idType || '',
          member.idNumber,
          member.gender,
          member.birthDay
        ];
      })

      // Combinar información, encabezados y datos de jugadores
      const exportData = [...infoData, ...playersData.sort()];

      const reportData = [{
        data: exportData,
        sheetName: team.name,
        skipHeader: true,
        formatTables: true
      }];

      const fileName = `Plantilla_Jugadores_${team.name}_${new Date().toISOString().split('T')[0]}`;
      this.reportService.exportExcel(reportData, fileName);
      
      this.messageService.add({
        severity: 'success',
        summary: 'Exportación exitosa',
        detail: `Se exportaron ${members.length} jugadores del equipo ${team.name}`,
        life: 3000,
      });
      
      this.loadingService.hideLoading();
    }).catch((error) => {
      this.loadingService.hideLoading();
      this.messageService.add({
        severity: 'error',
        summary: 'Error en exportación',
        detail: 'No se pudo exportar la plantilla de jugadores',
        life: 5000,
      });
    });
  }

  private async getTeamMembersForCategory(categoryId: string): Promise<{member: Member, team: TeamRegistration, jerseyNumber?: string}[]> {
    const allMembers: {member: Member, team: TeamRegistration, jerseyNumber?: string}[] = [];
    const teamsInCategory = this.sortedTeamRegistrations.filter(team => 
      team.categoryId === categoryId && team.members && team.members.length > 0
    );

    for (const team of teamsInCategory) {
      if (team.members) {
        const memberIds = team.members.map(m => m.memberId);
        const teamMembers = await this.getTeamMembers(memberIds);
        
        // Add team information and jersey number to each member
        teamMembers.forEach(member => {
          const teamMemberData = team.members?.find(m => m.memberId === member.memberId);
          const jerseyNumber = teamMemberData?.customData?.jerseyNumber || teamMemberData?.customData?.numeroJuego || '';
          
          allMembers.push({
            member: member,
            team: team,
            jerseyNumber: jerseyNumber
          });
        });
      }
    }

    return allMembers;
  }

  private async getTeamMembers(memberIds: string[]): Promise<Member[]> {
    return new Promise((resolve, reject) => {
      if (memberIds.length === 0) {
        resolve([]);
        return;
      }

      const memberConditionals = memberIds.map((memberId, index) => ({
        conditionalConnector: 'OR',
        logicOperator: '=',
        evaluatedProperty: 'id',
        parameterName: `@MemberId${index}`,
        parameterValue: memberId,
      }));

      const options: QueryOptionDto = {
        options: {
          conditionals: [...memberConditionals],
        },
      };

      const entity: AdministratorEntity = {
        type: AcademyAdministratorType.MEMBERS,
        operation: AcademyAdministratorOperation.SELECT,
        payload: options,
      };

      this.gestionService
        .sendRequest(entity)
        .pipe(catchError((error) => {
          reject(error);
          return throwError(() => error);
        }))
        .subscribe((response: HttpResponse) => {
          if (response.code === 200 && response.response) {
            resolve(response.response as Member[]);
          } else {
            resolve([]);
          }
        });
    });
  }

  private getTeamNameForMember(memberId: string): string {
    const team = this.sortedTeamRegistrations.find(t => 
      t.members?.some(m => m.memberId === memberId)
    );
    return team?.name || '';
  }

  // ---- Crosseling methods ----

  getTeamCrosselingAmount(team: TeamRegistration): number {
    if (!team.customData?.crosselingItems) return 0;
    return team.customData.crosselingItems
      .filter((item: any) => item.isIncluded)
      .reduce((sum: number, item: any) => sum + item.amount, 0);
  }

  getMembersCrosselingAmount(team: TeamRegistration): number {
    if (!team.members) return 0;
    let total = 0;
    for (const member of team.members) {
      if (member.customData?.crosselingItems) {
        total += member.customData.crosselingItems
          .filter((item: any) => item.isIncluded)
          .reduce((sum: number, item: any) => sum + item.amount, 0);
      }
    }
    return total;
  }

  getTotalCrosselingAmount(team: TeamRegistration): number {
    return this.getTeamCrosselingAmount(team) + this.getMembersCrosselingAmount(team);
  }

  getTeamCrosselingDescription(team: TeamRegistration): string {
    const items: string[] = [];
    if (team.customData?.crosselingItems) {
      for (const item of team.customData.crosselingItems) {
        if (item.isIncluded) {
          items.push(`${item.name} ($${item.amount.toLocaleString()}) [Equipo]`);
        }
      }
    }
    if (team.members) {
      for (const member of team.members) {
        if (member.customData?.crosselingItems) {
          for (const item of member.customData.crosselingItems) {
            if (item.isIncluded) {
              items.push(`${item.name} ($${item.amount.toLocaleString()}) [${member.memberId}]`);
            }
          }
        }
      }
    }
    return items.join(', ') || '';
  }

  // ---- Financial Report Export ----

  exportFinancialReport(): void {
    if (this.sortedTeamRegistrations.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Sin datos',
        detail: 'No hay equipos registrados para exportar',
        life: 3000,
      });
      return;
    }

    const reportData: any[] = [];

    for (const team of this.sortedTeamRegistrations) {
      const categoryName = this.competitionCategories.find(cat => cat.id === team.categoryId)?.name || '';
      const clubName = this.getClubName(team.clubId);
      const inscriptionFee = this.getInscriptionFee(team.categoryId);

      const isPerAthlete = this.competition.customData?.['paymentModel'] === 'PER_ATHLETE';
      const memberCount = team.members?.filter(m => m.isActive).length || 0;
      const totalInscription = isPerAthlete ? inscriptionFee * memberCount : inscriptionFee;

      const row: any = {
        'Equipo': team.name,
        'Categoría': categoryName,
        'Club': clubName,
        'Modelo Cobro': isPerAthlete ? 'Por Atleta' : 'Por Equipo',
        'Miembros': memberCount,
        'Tarifa Base': inscriptionFee,
        'Total Inscripción': totalInscription,
        'Estado Pago': team.payment.paymentStatus === 'PAYMENT_PENDING' ? 'Pendiente' : team.payment.paymentStatus === 'PAYMENT_COMPLETED' ? 'Pagado' : team.payment.paymentStatus,
      };

      if (this.competition.hasCrosselling) {
        row['Crosseling Equipo'] = this.getTeamCrosselingAmount(team);
        row['Crosseling Atletas'] = this.getMembersCrosselingAmount(team);
        row['Total Crosseling'] = this.getTotalCrosselingAmount(team);
        row['Detalle Crosseling'] = this.getTeamCrosselingDescription(team);
      }

      row['Total'] = totalInscription + (this.competition.hasCrosselling ? this.getTotalCrosselingAmount(team) : 0);

      reportData.push(row);
    }

    const reports: ExcelReport[] = [{
      data: reportData,
      sheetName: 'Reporte Financiero'
    }];

    // Add crosseling detail sheet if applicable
    if (this.competition.hasCrosselling) {
      const detailData: any[] = [];
      for (const team of this.sortedTeamRegistrations) {
        // PER_TEAM items
        if (team.customData?.crosselingItems) {
          for (const item of team.customData.crosselingItems) {
            detailData.push({
              'Equipo': team.name,
              'Tipo': 'Por Equipo',
              'Atleta': '—',
              'Item': item.name,
              'Precio': item.amount,
              'Incluido': item.isIncluded ? 'Sí' : 'No'
            });
          }
        }
        // PER_ATHLETE items
        if (team.members) {
          for (const member of team.members) {
            if (member.customData?.crosselingItems) {
              for (const item of member.customData.crosselingItems) {
                detailData.push({
                  'Equipo': team.name,
                  'Tipo': 'Por Atleta',
                  'Atleta': member.memberId,
                  'Item': item.name,
                  'Precio': item.amount,
                  'Incluido': item.isIncluded ? 'Sí' : 'No'
                });
              }
            }
          }
        }
      }
      reports.push({
        data: detailData,
        sheetName: 'Detalle Crosseling'
      });
    }

    this.reportService.exportExcel(reports, `Reporte_Financiero_Hockey_${this.competition.name}`);

    this.messageService.add({
      severity: 'success',
      summary: 'Exportación exitosa',
      detail: 'Se exportó el reporte financiero correctamente',
      life: 3000,
    });
  }

  handleError(error: HttpErrorResponse) {
    this.loadingService.hideLoading();
    if (error.status === 422) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Sin resultados',
        detail: 'No se encontraron resultados para la búsqueda realizada',
        life: 5000,
      });
    } else if (error.status === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail:
          'No se pudo establecer conexión con el servidor, por favor inténtalo nuevamente',
        life: 5000,
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error.message,
        life: 5000,
      });
    }
    return throwError(
      () => new Error('Something bad happened; please try again later.')
    );
  }
}
